import { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { CheckRounded as DoneIcon } from "@mui/icons-material";
import { useRecordContext } from "react-admin";
import { DocumentRead, DocumentStatus } from "@/types";
import { isInFlightStatus } from "@/constants/documentStatus";
import { formatDate } from "@/utils/formatters/dateFormatter";

/**
 * The milestones a document passes through. The active one carries the single
 * accent; earlier ones read as done, later ones are dimmed (convention 4: a
 * dated milestone trail over a graphical stepper).
 */
export const PROCESSING_TRAIL = ["Uploaded", "Extracting fields", "Review", "Loading records"] as const;

export interface TrailStep {
  /** Index into PROCESSING_TRAIL of the step currently in progress. */
  activeIndex: number;
  /** What the active step is doing right now, in the user's words. */
  activeLabel: string;
}

/**
 * Which milestone an in-flight status sits on and how to describe it. Null for
 * every status the backend will not move on its own (review, completed, failed,
 * rejected): the indicator renders nothing for those.
 */
export const resolveTrailStep = (status: DocumentStatus | undefined): TrailStep | null => {
  switch (status) {
    case DocumentStatus.UPLOADED:
      return { activeIndex: 1, activeLabel: "Queued for extraction" };
    case DocumentStatus.PROCESSING:
      return { activeIndex: 1, activeLabel: "Extracting fields" };
    case DocumentStatus.REPROCESS:
      return { activeIndex: 1, activeLabel: "Queued for reprocessing" };
    case DocumentStatus.APPROVED:
      return { activeIndex: 3, activeLabel: "Queued to load" };
    case DocumentStatus.LOADING:
      return { activeIndex: 3, activeLabel: "Loading records" };
    case DocumentStatus.LOADED:
      return { activeIndex: 3, activeLabel: "Finalizing" };
    default:
      return null;
  }
};

/** "42 s" · "3 min 12 s" · "1 h 4 min" — elapsed time in the active step. */
export const formatElapsed = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h} h ${m} min`;
  if (m > 0) return `${m} min ${s} s`;
  return `${s} s`;
};

/** "every 2 s" · "every 15 s" — the stated cadence beside Check now. */
export const formatCadence = (intervalMs: number | false): string | null =>
  intervalMs ? `checks every ${Math.round(intervalMs / 1000)} s` : null;

/** Once a second while mounted, so the elapsed figure ticks. */
const useTicker = (enabled: boolean): number => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(id);
  }, [enabled]);
  return now;
};

interface DocumentProcessingIndicatorProps {
  /** Current poll interval from useDocumentStatusPolling; false when not polling. */
  pollIntervalMs?: number | false;
  /** Refetch now. Omitted → the Check now control is not rendered. */
  onCheckNow?: () => void;
}

/**
 * In-flight trail for a Document show page: "✓ Uploaded 3:08 PM → ◌ Extracting
 * fields · 42 s → Review → Loading records", with the poll cadence and a Check
 * now control on the right. Renders only while the document is queued,
 * processing or loading; `useDocumentStatusPolling` advances the status and the
 * trail disappears at a terminal state.
 *
 * Elapsed time counts from the document's last write (`modified_datetime`,
 * which the status flip updates), falling back to when this page mounted, so a
 * reader arriving mid-extraction still sees how long it has run.
 */
export const DocumentProcessingIndicator = ({
  pollIntervalMs = false,
  onCheckNow,
}: DocumentProcessingIndicatorProps) => {
  const documentRecord = useRecordContext<DocumentRead>();
  const status = documentRecord?.status;
  const isInFlight = isInFlightStatus(status);
  const now = useTicker(isInFlight);
  const [mountedAt] = useState(() => Date.now());
  const step = resolveTrailStep(status);
  if (!isInFlight || !step) return null;

  const since = documentRecord?.modified_datetime ? Date.parse(String(documentRecord.modified_datetime)) : NaN;
  const elapsedMs = now - (Number.isFinite(since) ? since : mountedAt);
  const uploadedAt = documentRecord?.uploaded_datetime
    ? formatDate(String(documentRecord.uploaded_datetime), "h:mm a")
    : null;
  const cadence = formatCadence(pollIntervalMs);

  return (
    <Box px={5} py={1.5} data-testid="document-processing-indicator">
      <Stack direction="row" alignItems="center" flexWrap="wrap" rowGap={0.5} columnGap={1.25}>
        {PROCESSING_TRAIL.map((label, index) => {
          const isDone = index < step.activeIndex;
          const isActive = index === step.activeIndex;
          return (
            <Stack key={label} direction="row" alignItems="center" columnGap={1.25}>
              {index > 0 && (
                <Typography component="span" variant="body2" sx={{ color: "text.disabled" }} aria-hidden="true">
                  →
                </Typography>
              )}
              <Stack direction="row" alignItems="center" columnGap={0.75}>
                {isDone && <DoneIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
                {isActive && <CircularProgress size={14} thickness={5} />}
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? "text.primary" : isDone ? "text.secondary" : "text.disabled",
                  }}
                >
                  {isActive ? step.activeLabel : label}
                  {isDone && index === 0 && uploadedAt ? ` ${uploadedAt}` : ""}
                  {isActive ? ` · ${formatElapsed(elapsedMs)}` : ""}
                </Typography>
              </Stack>
            </Stack>
          );
        })}
        <Stack direction="row" alignItems="center" columnGap={1} sx={{ ml: "auto" }}>
          {cadence && (
            <Typography variant="caption" color="text.secondary">
              {cadence}
            </Typography>
          )}
          {onCheckNow && (
            <Button size="small" variant="text" onClick={onCheckNow} sx={{ minWidth: 0, px: 1, py: 0.25 }}>
              Check now
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};
